import { getBackendUrl } from '../utils/api';
import React, { useState, useMemo } from 'react';
import axios from 'axios';

const AdminRadioChartUpload = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [parsedSongs, setParsedSongs] = useState([]);
  const [allSongs, setAllSongs] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState('current'); // 'current' or 'next'
  const [recategorizing, setRecategorizing] = useState(false);
  const [recategorizeResult, setRecategorizeResult] = useState(null);

  // Calculate week options
  const weekOptions = useMemo(() => {
    const now = new Date();
    const options = [];
    
    // Helper to get Monday of a week
    const getMondayOfWeek = (date) => {
      const d = new Date(date);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
      return new Date(d.setDate(diff));
    };
    
    // Helper to get Sunday of a week
    const getSundayOfWeek = (monday) => {
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return sunday;
    };
    
    // Helper to get ISO week number
    const getWeekNumber = (date) => {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    };
    
    // Format date as DD-MM-YY
    const formatDate = (date) => {
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yy = String(date.getFullYear()).slice(-2);
      return `${dd}-${mm}-${yy}`;
    };
    
    // Current week
    const currentMonday = getMondayOfWeek(now);
    const currentSunday = getSundayOfWeek(currentMonday);
    const currentWeekNum = getWeekNumber(currentMonday);
    const currentYear = currentMonday.getFullYear();
    
    options.push({
      value: 'current',
      label: `Τρέχουσα Εβδομάδα ${currentWeekNum}: ${formatDate(currentMonday)} έως ${formatDate(currentSunday)}`,
      weekNumber: currentWeekNum,
      year: currentYear,
      weekStart: currentMonday.toISOString().split('T')[0],
      weekId: `${currentYear}-W${String(currentWeekNum).padStart(2, '0')}`
    });
    
    // Next week
    const nextMonday = new Date(currentMonday);
    nextMonday.setDate(nextMonday.getDate() + 7);
    const nextSunday = getSundayOfWeek(nextMonday);
    const nextWeekNum = getWeekNumber(nextMonday);
    const nextYear = nextMonday.getFullYear();
    
    options.push({
      value: 'next',
      label: `Επόμενη Εβδομάδα ${nextWeekNum}: ${formatDate(nextMonday)} έως ${formatDate(nextSunday)}`,
      weekNumber: nextWeekNum,
      year: nextYear,
      weekStart: nextMonday.toISOString().split('T')[0],
      weekId: `${nextYear}-W${String(nextWeekNum).padStart(2, '0')}`
    });
    
    return options;
  }, []);

  const selectedWeekData = weekOptions.find(w => w.value === selectedWeek);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setError(null);
    } else {
      setError('Παρακαλώ επιλέξτε ένα έγκυρο CSV αρχείο');
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Παρακαλώ επιλέξτε ένα αρχείο');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const backendUrl = getBackendUrl();
      const response = await axios.post(
        `${backendUrl}/api/admin/radio-chart/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setParsedSongs(response.data.preview || []);
      setAllSongs(response.data.songs || []);
      setPreview({
        total: response.data.total_songs,
        date: response.data.upload_date
      });
      setSuccess(`✅ Επιτυχής ανάλυση! Βρέθηκαν ${response.data.total_songs} τραγούδια.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα κατά την ανάγνωση του αρχείου');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (allSongs.length === 0) {
      setError('Δεν υπάρχουν δεδομένα για αποθήκευση');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      // Save all songs with selected week data
      const backendUrl = getBackendUrl();
      const saveResponse = await axios.post(
        `${backendUrl}/api/admin/radio-chart/save`,
        {
          songs: allSongs,
          limit: 1000,
          // Pass selected week information
          week_number: selectedWeekData.weekNumber,
          year: selectedWeekData.year,
          week_start: selectedWeekData.weekStart,
          week_id: selectedWeekData.weekId
        }
      );

      setSuccess(`✅ Επιτυχής αποθήκευση για ${selectedWeekData.label}! Αποθηκεύτηκαν ${saveResponse.data.total_songs} τραγούδια.`);
      
      // Reset after 3 seconds
      setTimeout(() => {
        setFile(null);
        setPreview(null);
        setParsedSongs([]);
        setAllSongs([]);
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα κατά την αποθήκευση');
    } finally {
      setUploading(false);
    }
  };

  // Recategorize all songs with updated logic
  const handleRecategorize = async () => {
    setRecategorizing(true);
    setError(null);
    setRecategorizeResult(null);
    
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${backendUrl}/api/admin/radio-chart/recategorize`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setRecategorizeResult(response.data);
      setSuccess(`✅ Ολοκληρώθηκε! ${response.data.changes_made} αλλαγές κατηγοριοποίησης`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα κατά την ανακατηγοριοποίηση');
    } finally {
      setRecategorizing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">
            📻 Radio Top Songs - Upload CSV
          </h1>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition"
          >
            ← Επιστροφή
          </button>
        </div>

        {/* Week Selector */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <label className="block text-sm font-semibold text-blue-900 mb-3">
            📅 Επιλέξτε Εβδομάδα για το Chart
          </label>
          <div className="space-y-2">
            {weekOptions.map((week) => (
              <label 
                key={week.value}
                className={`flex items-center p-3 rounded-lg cursor-pointer transition border-2 ${
                  selectedWeek === week.value 
                    ? 'bg-blue-100 border-blue-500' 
                    : 'bg-white border-gray-200 hover:border-blue-300'
                }`}
              >
                <input
                  type="radio"
                  name="week"
                  value={week.value}
                  checked={selectedWeek === week.value}
                  onChange={(e) => setSelectedWeek(e.target.value)}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <span className={`ml-3 font-medium ${
                  selectedWeek === week.value ? 'text-blue-900 dark:text-blue-200' : 'text-gray-700 dark:text-gray-300'
                }`}>
                  {week.label}
                </span>
                {week.value === 'next' && (
                  <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded">
                    ΠΡΟΓΡΑΜΜΑΤΙΣΜΕΝΟ
                  </span>
                )}
              </label>
            ))}
          </div>
          <p className="text-xs text-blue-700 mt-2">
            💡 Μπορείτε να ανεβάσετε το chart για την επόμενη εβδομάδα εκ των προτέρων
          </p>
        </div>

        {/* File Upload Section */}
        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Επιλέξτε CSV Αρχείο
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
            />
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className={`px-6 py-2 rounded-md font-semibold text-white
                ${!file || uploading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
                }`}
            >
              {uploading ? 'Ανάλυση...' : 'Ανάλυση'}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
            <p className="text-green-700 text-sm">{success}</p>
          </div>
        )}

        {/* Preview Section */}
        {preview && (
          <div className="mb-8">
            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">
                📊 Προεπισκόπηση Δεδομένων
              </h2>
              
              {/* Selected Week Info */}
              <div className="mb-4 p-3 bg-blue-100 rounded-lg">
                <p className="text-sm font-semibold text-blue-900">
                  📅 Θα αποθηκευτεί για: {selectedWeekData?.label}
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  Week ID: {selectedWeekData?.weekId}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-600">Σύνολο Τραγουδιών</p>
                  <p className="text-2xl font-bold text-blue-600">{preview.total}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Θα Αποθηκευτούν</p>
                  <p className="text-2xl font-bold text-green-600">
                    {Math.min(1000, preview.total)}
                  </p>
                </div>
              </div>

              {/* Sample Songs */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Change</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Song</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Artist</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Days</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {parsedSongs.map((song, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {song.rank}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                          <span className={`font-semibold ${
                            song.change > 0 ? 'text-green-600 dark:text-green-400' :
                            song.change < 0 ? 'text-red-600 dark:text-red-400' :
                            'text-gray-500 dark:text-gray-400'
                          }`}>
                            {song.change > 0 ? `+${song.change}` : song.change}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{song.song}</td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{song.artists}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {song.days_on_chart}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                * Εμφανίζονται τα πρώτα 10 τραγούδια για προεπισκόπηση
              </p>
            </div>

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={uploading}
              className={`w-full px-6 py-3 rounded-md font-semibold text-white text-lg
                ${uploading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
                }`}
            >
              {uploading ? 'Αποθήκευση...' : `💾 Αποθήκευση για ${selectedWeekData?.label.split(':')[0]} (${allSongs.length} Τραγούδια)`}
            </button>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/50 rounded-lg border border-blue-200 dark:border-blue-700">
          <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
            📋 Οδηγίες:
          </h3>
          <ul className="text-sm text-blue-800 dark:text-blue-100 space-y-1">
            <li>1. <strong>Επιλέξτε την εβδομάδα</strong> για την οποία ανεβάζετε το chart</li>
            <li>2. Επιλέξτε το CSV αρχείο με τα Radio Top Songs</li>
            <li>3. Πατήστε &quot;Ανάλυση&quot; για να ελέγξετε τα δεδομένα</li>
            <li>4. Δείτε την προεπισκόπηση των δεδομένων</li>
            <li>5. Πατήστε &quot;Αποθήκευση&quot; για να ενημερώσετε τη λίστα</li>
          </ul>
          <div className="mt-3 p-2 bg-green-50 dark:bg-green-900/50 rounded border border-green-200 dark:border-green-600">
            <p className="text-xs text-green-800 dark:text-green-100">
              💡 <strong>Tip:</strong> Μπορείτε να ανεβάσετε το chart της επόμενης εβδομάδας πριν ξεκινήσει, 
              ώστε να είναι έτοιμο για τη Δευτέρα!
            </p>
          </div>
        </div>

        {/* Recategorize Section */}
        <div className="mt-6 p-4 bg-purple-50 dark:bg-purple-900/50 rounded-lg border border-purple-200 dark:border-purple-600">
          <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-2">
            🔄 Ανακατηγοριοποίηση Τραγουδιών (Λογική v3 - ΤΙΤΛΟΣ ΠΡΩΤΑ)
          </h3>
          <div className="text-sm text-purple-800 dark:text-purple-100 mb-3 space-y-2">
            <p><strong>Λογική Κατηγοριοποίησης:</strong></p>
            <ol className="list-decimal list-inside text-xs space-y-1 ml-2">
              <li><strong>ΒΗΜΑ 1:</strong> Αν ο ΤΙΤΛΟΣ έχει ελληνικά γράμματα (π.χ. "Εξαίρεση") → <span className="text-green-600 dark:text-green-400">ΕΛΛΗΝΙΚΟ</span></li>
              <li><strong>ΒΗΜΑ 2:</strong> Αν ο ΤΙΤΛΟΣ είναι greeklish (π.χ. "Alleluia", "Exairesi", "Kornaro") → <span className="text-green-600 dark:text-green-400">ΕΛΛΗΝΙΚΟ</span></li>
              <li><strong>ΒΗΜΑ 3:</strong> Αλλιώς → <span className="text-blue-600 dark:text-blue-400">ΔΙΕΘΝΕΣ</span></li>
            </ol>
            <p className="text-xs mt-2 p-2 bg-yellow-100 dark:bg-yellow-900/50 rounded">
              ⚠️ <strong>Ο ΤΙΤΛΟΣ καθορίζει την κατηγορία, ΟΧΙ ο καλλιτέχνης!</strong><br/>
              • "Naked" by Playmen → ΔΙΕΘΝΕΣ (αγγλικός τίτλος)<br/>
              • "Alleluia" by Arcade → ΕΛΛΗΝΙΚΟ (greeklish = Αλληλούια)
            </p>
          </div>
          <button
            onClick={handleRecategorize}
            disabled={recategorizing}
            className={`px-4 py-2 rounded-lg font-semibold transition ${
              recategorizing 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
          >
            {recategorizing ? '⏳ Επεξεργασία...' : '🔄 Ανακατηγοριοποίηση τραγουδιών'}
          </button>
          
          {recategorizeResult && (
            <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded border border-purple-300 dark:border-purple-500">
              <p className="text-sm font-semibold text-purple-900 dark:text-white">
                Αποτελέσματα:
              </p>
              <ul className="text-sm text-purple-800 dark:text-purple-100 mt-1">
                <li>🇬🇷 Ελληνικά: <strong>{recategorizeResult.new_greek_count}</strong></li>
                <li>🌍 Διεθνή: <strong>{recategorizeResult.new_international_count}</strong></li>
                <li>🔄 Αλλαγές: <strong>{recategorizeResult.changes_made}</strong></li>
              </ul>
              {recategorizeResult.changes_made > 0 && recategorizeResult.changes && (
                <details className="mt-2">
                  <summary className="text-xs text-purple-600 dark:text-purple-300 cursor-pointer">
                    Δείτε όλες τις αλλαγές ({recategorizeResult.changes.length})
                  </summary>
                  <ul className="text-xs mt-1 space-y-1 max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded p-2">
                    {recategorizeResult.changes.map((change, idx) => (
                      <li key={idx} className="text-gray-700 dark:text-gray-200 py-1 border-b border-gray-100 dark:border-gray-800 last:border-0">
                        <span className="font-medium">{idx + 1}.</span> {change.song} - {change.artist || 'Unknown'}: <span className="text-red-500 font-medium">{change.old}</span> → <span className="text-green-500 font-medium">{change.new}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminRadioChartUpload;
